import { useState } from 'react'
import { X, CheckCircle, Truck, Euro, AlertTriangle } from 'lucide-react'
import api from '@/lib/api'
import { useToast } from '@/components/ui/Toast'

interface Props {
  isOpen: boolean
  onClose: () => void
  returnData: {
    id: number
    code: string
    customer_name: string
    customer_type: string
    product_amount: number
    shipping_cost: number
    refund_amount: number
  }
  onApproved: () => void
}

const CARRIERS = [
  { code: 'mrw', name: 'MRW' },
  { code: 'gls', name: 'GLS' },
  { code: 'ctt', name: 'CTT Express' }
]

export default function ApproveReturnModal({ isOpen, onClose, returnData, onApproved }: Props) {
  const [carrier, setCarrier] = useState('mrw')
  const [notes, setNotes] = useState('')
  const [loading, setLoading] = useState(false)
  const toast = useToast()

  const formatMoney = (amount: number) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount)
  }

  const handleApprove = async () => {
    setLoading(true)
    try {
      await api.post(`/returns/admin/${returnData.id}/approve`, {
        carrier_code: carrier,
        internal_notes: notes || undefined
      })

      toast.success('Aprobada', `Devolucion ${returnData.code} aprobada correctamente`)

      onApproved()
      onClose()
    } catch (error: any) {
      toast.error('Error', error.message || 'Error al aprobar la devolucion')
    } finally {
      setLoading(false)
    }
  }

  if (!isOpen) return null

  const isPVP = returnData.customer_type === 'PVP'

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl max-w-md w-full">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900 dark:text-white">
                Aprobar Devolucion
              </h2>
              <p className="text-sm text-slate-500">{returnData.code}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* Resumen de importes */}
          <div className="bg-slate-50 dark:bg-slate-900 rounded-xl p-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-slate-500">Importe productos:</span>
              <span className="font-medium">{formatMoney(returnData.product_amount)}</span>
            </div>
            {returnData.shipping_cost > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Coste envio (PVP):</span>
                <span className="font-medium text-red-600">-{formatMoney(returnData.shipping_cost)}</span>
              </div>
            )}
            <div className="flex justify-between text-lg font-bold border-t border-slate-200 dark:border-slate-700 pt-2 mt-2">
              <span>Reembolso:</span>
              <span className="text-green-600">{formatMoney(returnData.refund_amount)}</span>
            </div>
          </div>

          {/* Info segun tipo de cliente */}
          {isPVP ? (
            <div className="bg-amber-50 dark:bg-amber-900/20 rounded-xl p-4 border border-amber-200 dark:border-amber-800">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-amber-800 dark:text-amber-400">Cliente PVP</p>
                  <p className="text-amber-700 dark:text-amber-300 mt-1">
                    El cliente debe enviar el paquete por su cuenta. El coste de envio ({formatMoney(returnData.shipping_cost)}) se descontara del reembolso.
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-green-50 dark:bg-green-900/20 rounded-xl p-4 border border-green-200 dark:border-green-800">
              <div className="flex items-start gap-3">
                <Truck className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-green-800 dark:text-green-400">Cliente {returnData.customer_type}</p>
                  <p className="text-green-700 dark:text-green-300 mt-1">
                    Se generara una etiqueta de recogida automaticamente. El transportista pasara a recoger el paquete.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Selector de transportista (solo para PVD/VIP) */}
          {!isPVP && (
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Transportista para recogida
              </label>
              <select
                value={carrier}
                onChange={(e) => setCarrier(e.target.value)}
                className="w-full px-4 py-2 rounded-lg border border-slate-200 dark:border-slate-700
                         bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
              >
                {CARRIERS.map(c => (
                  <option key={c.code} value={c.code}>{c.name}</option>
                ))}
              </select>
            </div>
          )}

          {/* Notas internas */}
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
              Notas internas (opcional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Notas visibles solo para el equipo..."
              rows={3}
              className="w-full px-4 py-2 rounded-lg border border-slate-200 dark:border-slate-700
                       bg-white dark:bg-slate-900 text-slate-900 dark:text-white resize-none"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-200 dark:border-slate-700">
          <button
            onClick={onClose}
            className="px-4 py-2 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg"
          >
            Cancelar
          </button>
          <button
            onClick={handleApprove}
            disabled={loading}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
          >
            {loading ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                Aprobando...
              </>
            ) : (
              <>
                <CheckCircle className="w-4 h-4" />
                Aprobar devolucion
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}
